import { v4 } from '@rotra-air-ocean/rotranext-shared-code';

import { adminFetch } from './adminFetch';

const API_ENDPOINT = process.env.VITE_MYROTRA_ADMIN_API_ENDPOINT;

export type WorkflowRunResult = 'failed' | 'success' | 'inProcess';
export type AttachmentProcessingStatus = 'not_required' | 'pending' | 'ready';
export type WorkflowRunAttachmentStatus =
  | 'pending'
  | 'processing'
  | 'processed'
  | 'unsupported'
  | 'failed';
export type WorkflowRunAttachmentAsset = 'original' | 'markdown' | 'text';

export interface WorkflowRunAttachment {
  attachmentId: string;
  name: string;
  contentType: string;
  size: number;
  isInline: boolean;
  contentId?: string | null;
  kind: 'file' | 'item' | 'reference';
  itemType?: 'message' | 'event' | 'contact';
  status: WorkflowRunAttachmentStatus;
  markdownAvailable: boolean;
  textAvailable: boolean;
  originalAvailable: boolean;
  parentAttachmentId?: string;
  nestingDepth: number;
  sourceOrder?: number;
  markdownCharacters?: number;
  textCharacters?: number;
  processingWarnings?: v4.AttachmentProcessingWarning[];
  error?: string;
}

export interface WorkflowRunAttachmentSummary {
  processingStatus: AttachmentProcessingStatus;
  total: number;
  processed: number;
  pending: number;
  processing: number;
  unsupported: number;
  failed: number;
  items: WorkflowRunAttachment[];
}

export interface WorkflowRunMessage {
  id: string;
  author:
    | {
        type: 'bert';
      }
    | {
        type: 'user';
        name: string;
        email: string;
      };
  text: string;
  stepId?: string;
  pending?: boolean;
}

export interface WorkflowRunListItem {
  id: string;
  emailId: string;
  workflowId: string;
  workflowVersion: number;
  result: WorkflowRunResult;
  mailbox: { id: string; mailAddress: string; userPrincipalName: string };
  subject: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  attachments: WorkflowRunAttachmentSummary;
}

export interface WorkflowRunDetail {
  run: WorkflowRunListItem & {
    status: 'active' | 'done' | 'failed';
    error?: string;
    failedStepId?: string | null;
    versions: Record<string, number>;
    messages: WorkflowRunMessage[];
    data: Record<string, unknown>;
    activeState: unknown;
    attachments: WorkflowRunAttachmentSummary;
  };
  workflowVersion: v4.WorkflowVersionSpec | null;
}

interface GetWorkflowRunsOptions {
  filters?: {
    search?: string;
    result?: WorkflowRunResult | '';
    workflowId?: string;
    workflowVersion?: number;
    startDate?: Date | null;
    endDate?: Date | null;
  };
  pagination?: {
    page: number;
    rowsPerPage: number;
  };
}

interface GetWorkflowRunsResponse {
  items: WorkflowRunListItem[];
  totalItems: number;
}

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.text().catch(() => 'Unknown error');
    throw new Error(`API error ${response.status}: ${error}`);
  }
  return response.json();
};

export const getWorkflowRuns = async (
  token: string,
  options: GetWorkflowRunsOptions,
): Promise<GetWorkflowRunsResponse> => {
  const offset = options.pagination?.page
    ? options.pagination.page * options.pagination.rowsPerPage
    : 0;
  const limit = options.pagination?.rowsPerPage || 25;
  const params = new URLSearchParams();

  params.set('offset', String(offset));
  params.set('limit', String(limit));

  if (options.filters?.result) params.set('result', options.filters.result);
  if (options.filters?.search) params.set('search', options.filters.search);
  if (options.filters?.workflowId)
    params.set('workflowId', options.filters.workflowId);
  if (options.filters?.workflowVersion)
    params.set('workflowVersion', String(options.filters.workflowVersion));
  if (options.filters?.startDate)
    params.set('startDate', new Date(options.filters.startDate).toISOString());
  if (options.filters?.endDate)
    params.set('endDate', new Date(options.filters.endDate).toISOString());

  const response = await adminFetch(
    `${API_ENDPOINT}/mail-automation/workflow-runs?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  );

  return handleResponse<GetWorkflowRunsResponse>(response);
};

export const getWorkflowRun = async (
  token: string,
  id: string,
): Promise<WorkflowRunDetail> => {
  const response = await adminFetch(
    `${API_ENDPOINT}/mail-automation/workflow-runs/${encodeURIComponent(id)}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  );

  return handleResponse<WorkflowRunDetail>(response);
};

export const getWorkflowRunAttachment = async (
  token: string,
  workflowRunId: string,
  attachmentId: string,
  asset: WorkflowRunAttachmentAsset,
): Promise<Response> => {
  const params = new URLSearchParams({ attachmentId });
  const response = await adminFetch(
    `${API_ENDPOINT}/mail-automation/workflow-runs/${encodeURIComponent(
      workflowRunId,
    )}/attachments/${asset}?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const error = await response.text().catch(() => 'Unknown error');
    throw new Error(`API error ${response.status}: ${error}`);
  }
  return response;
};
